import { Alert, Box, CircularProgress, Container, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useFindOrganizationBySlugQuery,
  useUpdateOrganizationMutation,
} from "@/libs/generated/hooks";
import {
  OrganizationFormSchema,
  OrganizationFormSchemaDefaultValues,
  type OrganizationFormSchemaType,
} from "@/schemas/organization-form";

import Form from "./Form";

function EditOrganization() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();

  if (!slug) {
    throw new Error("Slug is required");
  }
  const { data, isPending: loading, error } = useFindOrganizationBySlugQuery({ slug });
  const updateOrganization = useUpdateOrganizationMutation({
    onSuccess: (data) => {
      toast.success(t("network.organization.updated", { name: data.updateOrganization.name }), {
        position: "bottom-right",
      });
      client.invalidateQueries({ queryKey: ["findAllOrganizations"] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t("network.errors.unknown"), {
        position: "bottom-right",
      });
    },
  });
  const dataValues = data?.findOrganizationBySlug;
  const onSubmit = async (formData: OrganizationFormSchemaType) => {
    const ownerId = dataValues?.owner?.id;
    if (ownerId == null) throw new Error("Organization owner is missing");
    await updateOrganization.mutateAsync({
      UpdateOrganizationInput: { ...formData, owner: { ...formData.owner, id: ownerId } },
    });
    client.invalidateQueries({ queryKey: ["findOrganizationBySlug", { slug }] });
    client.invalidateQueries({ queryKey: ["findAllOrganizations"] });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading organization details :{" "}
        {error instanceof Error ? error.message : t("network.errors.unknown")}
      </Alert>
    );
  }

  if (!dataValues?.owner) return <Alert severity="error">Organization owner is missing</Alert>;
  const owner = dataValues.owner;
  if (owner.assigneeRank == null)
    return <Alert severity="error">Organization owner rank is missing</Alert>;
  const values: OrganizationFormSchemaType = {
    name: dataValues.name,
    slug: dataValues.slug,
    settings: OrganizationFormSchema.shape.settings.parse(dataValues.settings),
    owner: {
      id: owner.id,
      assigneeFirstName: owner.assigneeFirstName ?? "",
      assigneeLastName: owner.assigneeLastName ?? "",
      assigneeEmail: owner.assigneeEmail ?? "",
      assigneePhone: owner.assigneePhone ?? "",
      assigneeFunction: owner.assigneeFunction,
      assigneeService: owner.assigneeService,
      assigneeRank: owner.assigneeRank,
      address: {
        ...OrganizationFormSchemaDefaultValues.owner.address,
        ...(owner.address ?? {}),
        place_id: owner.address?.place_id ?? "",
        country: owner.address?.country ?? "",
      },
    },
  };
  return (
    <Container maxWidth="lg">
      <Grid container sx={{ position: "relative", top: "80px", paddingBottom: 10 }}>
        <Grid size={{ xs: 12 }}>
          <Form onSubmit={onSubmit} values={values} disabledFields={["assigneeEmail"]} />
        </Grid>
      </Grid>
    </Container>
  );
}

export default EditOrganization;
